/**
 * The New Initial Response experience: any ICS form the Planning P dashboard
 * offers, opening on the editable ICS-201.
 *
 * Built on the same `widgetRegistry` Planning P renders from, so a card behaves
 * identically in both places. What it deliberately leaves out is everything
 * belonging to the Planning P cycle itself: the phase stepper, the meeting
 * agenda and Advance Operational Period, none of which apply before
 * escalation.
 */

import React, { useEffect, useMemo, useState } from 'react';

import type { InitialResponseView } from 'utils/initialResponseView';

import { PREMIUM_RESOURCE_WIDGETS } from '../premiumResourceManagement';
import {
  PLANNING_PHASES,
  widgetsHiddenForFamily,
} from '../planningView/USCGPage';
import { useIcsFormFamily } from '../hooks/useIcsFormFamily';
import {
  buildFormsDropdownOptions,
  FormsDropdown,
  ICS_201_CARD_OPTION,
  ICS_201_WIDGET_NAME,
  RenderWidget,
  useWidgetRenderContext,
} from '../planningView/widgetRegistry';
import { usePremiumResourceManagementFlag } from 'utils/featureFlags';
import InitialResponseViewToggle from './InitialResponseViewToggle';
import { registerFocInitialResponseTourApi } from 'components/MapPage/focInitialResponseTour/focInitialResponseTourBridge';

/**
 * The agenda is a Planning P meeting aid, and `initialResponse` is the
 * read-only 201 PDF widget the editable card supersedes here.
 */
const EXCLUDED_WIDGETS = ['agenda', 'initialResponse'];

export const InitialResponseFormsView: React.FC<{
  /**
   * The Legacy/New toggle shares the dropdown row rather than sitting in a
   * strip of its own, so the view owns it. The Legacy stepper has no such row,
   * which is why it keeps rendering the toggle itself.
   */
  view: InitialResponseView;
  onViewChange: (next: InitialResponseView) => void;
}> = ({ view, onViewChange }) => {
  /**
   * Selection is held here rather than in `ICSContext.card`. That context is
   * mounted at app root and its `card` is shared with Planning P, so opening on
   * the 201 through it would also move the user's Planning P selection.
   */
  const [card, setCard] = useState<string>(ICS_201_WIDGET_NAME);
  const premiumResourceManagement = usePremiumResourceManagementFlag();
  const formFamily = useIcsFormFamily();

  const widgetContext = useWidgetRenderContext(PLANNING_PHASES, 0);

  const options = useMemo(
    () =>
      buildFormsDropdownOptions({
        exclude: [
          ...(premiumResourceManagement
            ? EXCLUDED_WIDGETS
            : [...EXCLUDED_WIDGETS, ...PREMIUM_RESOURCE_WIDGETS]),
          ...widgetsHiddenForFamily(formFamily),
        ],
        extra: [ICS_201_CARD_OPTION],
      }),
    [formFamily, premiumResourceManagement]
  );

  const cardToRender = options.some((option) => option.value === card)
    ? card
    : (options[0]?.value ?? ICS_201_WIDGET_NAME);

  // Page owns ensureNewIcs201 / openReportsSurface (survives map open). Card
  // selection lives here because the forms dropdown state is local.
  useEffect(() => {
    return registerFocInitialResponseTourApi({
      selectFormsCard: (widgetName) => setCard(widgetName),
    });
  }, []);

  return (
    <div
      className="initial-response-forms"
      data-testid="initial-response-forms-view"
    >
      <div
        className="d-flex align-items-center mb-2 flex-row flex-wrap gap-2"
        data-tour="incident-ir-forms"
      >
        <FormsDropdown
          options={options}
          value={cardToRender}
          onChange={setCard}
          id="initial-response-forms-dropdown"
        />
        <div className="ms-auto">
          <InitialResponseViewToggle view={view} onChange={onViewChange} />
        </div>
      </div>
      <div className="w-100">
        <RenderWidget widgetName={cardToRender} ctx={widgetContext} />
      </div>
    </div>
  );
};

export default InitialResponseFormsView;
